import { Card, Divider } from 'antd';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import LabItemForm from './LabItemsForm';
import LabItemTable from './LabItemsTable';
import { getPricing } from '@app/api/nominalPayments.api';

interface LabItemsProps {
  onItemsChange?: (items: { tests: any[]; products: any[]; vaccines: any[] }) => void;
  divisionOptions: { label: string; value: string }[];
  labItemsData?: {
    tests: any[];
    products: any[];
    vaccines: any[];
  };
  editable?: boolean;
  type: 'edit' | 'create' | 'view';
}

const LabItems: React.FC<LabItemsProps> = ({ onItemsChange, divisionOptions, labItemsData, editable, type }) => {
  const [items, setItems] = useState<any[]>([]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const hasManuallyAddedItems = useRef(false);

  const transformExistingData = (labItemsData: any) => {
    const transformedItems: any[] = [];

    // Helper function to transform a single item from API response
    const transformItem = (item: any, typeLabel: string) => {
      console.log('Raw item data:', {
        qty: item.qty,
        unit_price: item.unit_price,
        total: item.total,
        qtyType: typeof item.qty,
        unitPriceType: typeof item.unit_price,
      });
      // Only transform if item has the nested structure (from API)
      if (item.division && item.lab_service && item.lab_service.lab_service_category) {
        const unitPrice = Number(item.unit_price);
        const quantity = Number(item.qty) || 1;
        return {
          key: uuidv4(),
          department_id: item.department_id,
          type: item.type,
          lab_service_category_id: item.lab_service_category_id,
          lab_service_id: item.lab_service_id,
          qty: quantity,
          divisionLabel: `${item.division.code} - ${item.division.name}`,
          typeLabel: typeLabel,
          mainServiceLabel: `${item.lab_service.lab_service_category.code} - ${item.lab_service.lab_service_category.name}`,
          subServiceLabel: `${item.lab_service.code} - ${item.lab_service.name}`,
          uom: item.uom,
          unit_price: unitPrice,
          total: item.total || quantity * unitPrice,
          // Keep original item data for reference if needed
          originalItem: item,
        };
      }
      return null;
    };

    // Transform tests
    if (labItemsData?.tests) {
      labItemsData.tests.forEach((test: any) => {
        const transformedItem = transformItem(test, 'Test');
        if (transformedItem) {
          transformedItems.push(transformedItem);
        }
      });
    }

    // Transform products
    if (labItemsData?.products) {
      labItemsData.products.forEach((product: any) => {
        const transformedItem = transformItem(product, 'Product');
        if (transformedItem) {
          transformedItems.push(transformedItem);
        }
      });
    }

    // Transform vaccines
    if (labItemsData?.vaccines) {
      labItemsData.vaccines.forEach((vaccine: any) => {
        const transformedItem = transformItem(vaccine, 'Vaccine');
        if (transformedItem) {
          transformedItems.push(transformedItem);
        }
      });
    }

    return transformedItems;
  };

  const groupItemsByType = useCallback((items: any[]) => {
    const grouped = {
      tests: [] as any[],
      products: [] as any[],
      vaccines: [] as any[],
    };

    items.forEach((item) => {
      const type = item.typeLabel?.toLowerCase();
      if (type === 'test') grouped.tests.push(item);
      else if (type === 'product') grouped.products.push(item);
      else if (type === 'vaccine') grouped.vaccines.push(item);
    });

    return grouped;
  }, []);

  useEffect(() => {
    console.log('useEffect triggered:', {
      initialDataLoaded,
      hasLabItemsData: !!labItemsData,
      testsLength: labItemsData?.tests?.length,
      productsLength: labItemsData?.products?.length,
      vaccinesLength: labItemsData?.vaccines?.length,
      hasManuallyAddedItems: hasManuallyAddedItems.current,
    });

    if (!initialDataLoaded && !hasManuallyAddedItems.current) {
      if (
        labItemsData &&
        (labItemsData.tests?.length || labItemsData.products?.length || labItemsData.vaccines?.length)
      ) {
        console.log('Loading initial data...');
        const transformedItems = transformExistingData(labItemsData);
        console.log('Transformed items:', transformedItems);
        setItems(transformedItems);
        // Also notify parent about the initial data
        onItemsChange?.(groupItemsByType(transformedItems));
      }
      setInitialDataLoaded(true);
    }
  }, [labItemsData]);

  const handleAddItem = (item: any) => {
    console.log('Adding item:', item);
    hasManuallyAddedItems.current = true;
    const newItem = {
      ...item,
      key: uuidv4(),
    };
    setItems((prevItems) => {
      const updatedItems = [...prevItems, newItem];
      console.log('Updated items:', updatedItems);
      setTimeout(() => {
        onItemsChange?.(groupItemsByType(updatedItems));
      }, 0);
      return updatedItems;
    });
  };

  const handleRemoveItem = (key: string) => {
    console.log('Removing item:', key);
    setItems((prevItems) => {
      const updatedItems = prevItems.filter((item) => item.key !== key);
      setTimeout(() => {
        onItemsChange?.(groupItemsByType(updatedItems));
      }, 0);
      return updatedItems;
    });
  };

  const handleQtyChange = async (key: string, newQty: number) => {
    console.log('handleQtyChange called:', { key, newQty });

    try {
      // Find the item to update
      const itemIndex = items.findIndex((item) => item.key === key);
      if (itemIndex === -1) {
        console.error('Item not found:', key);
        throw new Error('Item not found');
      }

      const item = items[itemIndex];
      console.log('Found item:', item);

      // Call getPricing API with the lab service parameters
      const pricingResponse = await getPricing(
        item.type,
        null,
        null,
        null,
        item.department_id,
        null,
        newQty,
        'LAB',
        item.lab_service_id,
        null,
      );

      console.log('API response:', pricingResponse);

      const pricingData = pricingResponse?.data;

      if (!pricingData) {
        throw new Error('No pricing data received');
      }

      // Update the item with new values
      setItems((prevItems) => {
        const updatedItems = [...prevItems];
        const updatedItem = {
          ...item,
          qty: newQty,
          uom: pricingData.uom || item.uom,
          unit_price: Number(pricingData.unit_price),
          total: Number(pricingData.unit_price) * newQty,
        };

        updatedItems[itemIndex] = updatedItem;

        console.log('Updated item:', updatedItems[itemIndex]);

        // Notify parent about the changes
        setTimeout(() => {
          onItemsChange?.(groupItemsByType(updatedItems));
        }, 0);

        return updatedItems;
      });
    } catch (error) {
      console.error('Error in handleQtyChange:', error);
      throw error;
    }
  };

  return (
    <>
      {editable && (
        <>
          <Card title="Add Lab Items" size="small">
            <LabItemForm onAdd={handleAddItem} divisionOptions={divisionOptions} />
          </Card>
          <Divider />
        </>
      )}

      <LabItemTable
        data={items}
        onRemove={editable ? handleRemoveItem : undefined}
        editable={editable}
        type={type}
        onQtyChange={editable && type !== 'view' ? handleQtyChange : undefined}
      />
    </>
  );
};

export default LabItems;
